import React, { useCallback, useMemo, useRef, useState } from "react";
import { trackClickEvent } from "@foundation/nextjs-gtm";
import HeatmapTileComponent from "./HeatmapTile";
import styles from "./styles.module.css";
import squarifiedHeatmap from "~components/Heatmap/algo";
import type { HeatmapTile, HeatmapTileData } from "~types/types";

interface HeatmapProps<T> {
  readonly width: number;
  readonly height: number;
  readonly tilesData: HeatmapTileData<T>[];
  readonly enableTileFocusEvents?: boolean;
  readonly activeTileLabel?: string;
  readonly onTileClick?: (tile: HeatmapTile<T>) => void;
  readonly onTileFocus?: (tile: HeatmapTile<T>) => void;
  readonly onTileBlur?: (tile: HeatmapTile<T>) => void;
  readonly renderHoverContent?: (tile: HeatmapTile<T>) => React.ReactNode;
}

const HOVER_CONTAINER_MAX_WIDTH = 200;
const HOVER_CONTAINER_MAX_HEIGHT = 150;

const HOVER_CONTAINER_OFFSET_X = 5;
const HOVER_CONTAINER_OFFSET_Y = 5;

const computeHoverProperties = (
  cursorPosition: { x: number; y: number },
  containerRect: DOMRect
): React.CSSProperties => {
  const { x: cursorX, y: cursorY } = cursorPosition;

  // calculate hover horizontal placement
  const xAxisPlacement =
    cursorX - containerRect.left > containerRect.right - cursorX
      ? "right"
      : "left";

  // calculate hover vertical placement
  const yAxisPlacement =
    cursorY - containerRect.top > containerRect.bottom - cursorY
      ? "bottom"
      : "top";

  const hoverProperties: React.CSSProperties = {
    transform: "translate3d(0, 0, 0)",
    maxWidth: HOVER_CONTAINER_MAX_WIDTH,
    maxHeight: HOVER_CONTAINER_MAX_HEIGHT,
  };

  if (xAxisPlacement === "left") {
    hoverProperties.left =
      cursorX - containerRect.left + HOVER_CONTAINER_OFFSET_X;
  } else {
    hoverProperties.right =
      containerRect.right - cursorX + HOVER_CONTAINER_OFFSET_X;
  }

  if (yAxisPlacement === "top") {
    const top = cursorY - containerRect.top + HOVER_CONTAINER_OFFSET_Y;

    hoverProperties.top =
      top + HOVER_CONTAINER_MAX_HEIGHT > containerRect.height
        ? containerRect.height - HOVER_CONTAINER_MAX_HEIGHT
        : top;
  } else {
    const bottom = containerRect.bottom - cursorY + HOVER_CONTAINER_OFFSET_Y;

    hoverProperties.bottom =
      bottom + HOVER_CONTAINER_MAX_HEIGHT > containerRect.height ? 0 : bottom;
  }

  return hoverProperties;
};

export default function Heatmap<T>(props: HeatmapProps<T>) {
  const {
    width,
    height,
    tilesData,
    activeTileLabel,
    enableTileFocusEvents = false,
    onTileClick,
    onTileFocus,
    onTileBlur,
    renderHoverContent,
  } = props;

  const containerRef = useRef<HTMLDivElement>(null);
  const hoverContainerRef = useRef<HTMLDivElement>(null);

  const [hoverProperties, setHoverProperties] = useState<React.CSSProperties>(
    {}
  );

  const [hoverContent, setHoverContent] = useState<React.ReactNode>(null);

  const heatmapTiles: HeatmapTile<T>[] = useMemo(() => {
    // skip when data is not available
    if (!tilesData.length) {
      return [];
    }

    // const tiles = prepareHeatmapTiles(tilesData, width, height);
    const tiles = squarifiedHeatmap(tilesData, {
      x0: 0,
      y0: 0,
      x1: width,
      y1: height,
    });

    // filtering out all tiles with area = 0 before rendering because code will render
    // the padding for the tile and therefore will visually see this padding showing when
    // overflow is not hidden
    return tiles.filter((tile) => tile.width !== 0 && tile.height !== 0);
  }, [width, height, tilesData]);

  const renderHoverContentRef = useRef(renderHoverContent);

  renderHoverContentRef.current = renderHoverContent;

  const onTileFocusRef = useRef(onTileFocus);

  onTileFocusRef.current = onTileFocus;

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>, tile: HeatmapTile<T>) => {
      if (!containerRef.current) {
        return;
      }

      // render hover content
      if (renderHoverContentRef.current) {
        setHoverContent(renderHoverContentRef.current(tile));

        // get the container's bounding rectangle
        const containerRect = containerRef.current.getBoundingClientRect();

        const properties = computeHoverProperties(
          { x: e.clientX, y: e.clientY },
          containerRect
        );

        setHoverProperties(properties);
      }

      // trigger tile focus event
      onTileFocusRef.current?.(tile);
    },
    []
  );

  const handleMouseLeave = useCallback(
    (e: React.MouseEvent<HTMLDivElement>, tile: HeatmapTile<T>) => {
      trackClickEvent("chartHover", "hover_heatmap", "uiaction", "Market Data");
      trackClickEvent("chartHover", tile.data.label, "uiaction", "Market Data");

      // trigger tile blur event
      onTileBlur?.(tile);

      setHoverContent(null);
      setHoverProperties({});
    },
    [onTileBlur]
  );

  if (!heatmapTiles.length) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      className={styles.heatmap}
      style={{
        width,
        height,
      }}
    >
      {hoverContent && (
        <div
          ref={hoverContainerRef}
          className={styles.tileHover}
          style={hoverProperties}
        >
          {hoverContent}
        </div>
      )}

      {heatmapTiles.map((tile, index) => (
        <HeatmapTileComponent<T>
          key={index}
          tile={tile}
          onClick={onTileClick}
          activeTileLabel={activeTileLabel}
          enableTileFocusEvents={enableTileFocusEvents}
          handleMouseLeave={handleMouseLeave}
          handleMouseMove={handleMouseMove}
        />
      ))}
    </div>
  );
}
